import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Snackbar, Alert } from "@mui/material";
import { initializeSocket } from "../socket";
import { useBrand } from "../context/BrandContext";
import {
  useCoachExerciseLiveActions,
  deriveCoachControlIdleTimers,
} from "./coachExerciseLiveActions";
import { confirmFinalizeExercise } from "./confirmFinalizeExercise";
import {
  confirmSkipExercise,
  confirmSkipSequence,
} from "./confirmSkipTraining";
import { noteExerciseTimerRevision } from "../utils/sessionSync";
import {
  pickCoachFloatingBarExercise,
  enrichExerciseForCoachControls,
} from "./coachFloatingBarPickExercise";
import { CoachExerciseActionsBar } from "./CoachExerciseActionsBar";
import {
  resolveExerciseStateIdFromRow,
  TE_STATE_CANCELLED,
  TE_STATE_FINALIZED,
  TE_STATE_STARTED,
} from "../utils/coachExerciseNavigation";
import { isKotExercise } from "../utils/teExecutionType";
import { useKotCoachRound } from "../hooks/useKotCoachRound";
import {
  KotCoachStartRoundButton,
  KotCoachStopRoundButton,
} from "../components/KotCoachStartRoundBanner";
import { normalizeKotMode } from "../utils/kotCoachSubmit";
import "../components/KotCoachStartRound.css";

/**
 * Fixed top dock for Training room — staff/coach only (parent gates `visible`).
 * Kept at top so app bottom navigation stays visible.
 */
export function TrainingCoachFloatingBar({
  visible,
  coachLiveContext,
  selectedSession,
  selectedgrp,
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { brand } = useBrand();
  const brandPrimary = brand?.color_primary || "#0877BC";
  const brandPrimaryDark = brand?.color_primary_dark || "#065a8f";

  const token = localStorage.getItem("token");
  const socket = useMemo(() => initializeSocket(token), [token]);

  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const tsId =
    coachLiveContext?.tsId ?? selectedSession?.ts_id ?? null;

  const requestRefresh = useCallback(
    (te_id, exerciseStateUpdate) => {
      if (!te_id || !socket) return;
      if (exerciseStateUpdate) {
        noteExerciseTimerRevision(te_id, exerciseStateUpdate.exercise_timer_revision);
        return;
      }
      socket.emit("getStatus", { te_id });
    },
    [socket]
  );

  const {
    startExercise: emitStart,
    resumeExercise: emitResume,
    pauseExercise: emitPause,
    startNextSequence: emitStartNextSequence,
    skipSequence: emitSkipSequence,
    finalizeExercise: emitFinalize,
  } = useCoachExerciseLiveActions({
    socket,
    tsId,
    requestRefresh,
    coachRole: true,
  });

  const targetRaw = useMemo(() => {
    if (!coachLiveContext?.exercises?.length) return null;
    return pickCoachFloatingBarExercise(
      coachLiveContext.exercises,
      coachLiveContext.exerciseStates || {}
    );
  }, [coachLiveContext?.exercises, coachLiveContext?.exerciseStates]);

  const stateId = useMemo(() => {
    if (!targetRaw || !coachLiveContext?.exerciseStates) return null;
    return resolveExerciseStateIdFromRow(
      targetRaw,
      coachLiveContext.exerciseStates
    );
  }, [targetRaw, coachLiveContext?.exerciseStates]);

  const displayExercise = useMemo(() => {
    if (!targetRaw || !coachLiveContext) return null;
    const enriched = enrichExerciseForCoachControls(targetRaw, {
      exerciseStates: coachLiveContext.exerciseStates,
      exerciseTimers: coachLiveContext.exerciseTimers,
      sequenceTimers: coachLiveContext.sequenceTimers,
      currentSequences: coachLiveContext.currentSequences,
    });
    const rowForIdle = {
      ...targetRaw,
      ...enriched,
      te_state: stateId || targetRaw.te_state,
      tbl_state_te: targetRaw.tbl_state_te?.st_id
        ? targetRaw.tbl_state_te
        : stateId
          ? { st_id: stateId }
          : targetRaw.tbl_state_te,
    };
    const idle = deriveCoachControlIdleTimers(rowForIdle);
    return {
      ...enriched,
      timer:
        enriched.timer !== undefined && enriched.timer !== null
          ? enriched.timer
          : idle?.timer ?? 0,
      sequenceTimer:
        enriched.sequenceTimer !== undefined &&
        enriched.sequenceTimer !== null
          ? enriched.sequenceTimer
          : idle?.sequenceTimer ?? 0,
    };
  }, [targetRaw, coachLiveContext, stateId]);

  const startExercise = useCallback(
    (te_id) => {
      emitStart(te_id);
      setSnack({
        open: true,
        message: t("exerciseStarted") || "Exercise started",
        severity: "success",
      });
    },
    [emitStart, t]
  );

  const pauseExercise = useCallback(
    (te_id) => {
      emitPause(te_id);
      setSnack({
        open: true,
        message: t("exercisePaused") || "Exercise paused",
        severity: "success",
      });
    },
    [emitPause, t]
  );

  const resumeExercise = useCallback(
    (te_id) => {
      const remaining =
        displayExercise?.timer != null && displayExercise.timer !== ""
          ? Math.max(0, Math.floor(Number(displayExercise.timer)))
          : 0;
      emitResume(te_id, remaining);
      setSnack({
        open: true,
        message: t("exerciseResumed") || "Exercise resumed",
        severity: "success",
      });
    },
    [emitResume, displayExercise?.timer, t]
  );

  const startNextSequence = useCallback(
    (te_id) => {
      emitStartNextSequence(te_id);
    },
    [emitStartNextSequence]
  );

  const skipSequence = useCallback(
    (te_id) => {
      const confirmed = confirmSkipSequence({
        sequences: displayExercise?.sequences,
        currentSequence: displayExercise?.currentSequence,
        t,
      });
      if (!confirmed) return;
      emitSkipSequence(te_id);
    },
    [
      displayExercise?.sequences,
      displayExercise?.currentSequence,
      emitSkipSequence,
      t,
    ]
  );

  const skipExercise = useCallback(
    async (te_id) => {
      const list = [...(coachLiveContext?.exercises || [])].sort(
        (a, b) => (a.te_order ?? 0) - (b.te_order ?? 0)
      );
      const idx = list.findIndex(
        (ex) => String(ex.te_id) === String(te_id || displayExercise?.te_id)
      );
      const hasNext = idx >= 0 && idx < list.length - 1;
      const confirmed = confirmSkipExercise({ hasNext, t });
      if (!confirmed) return;
      const response = await emitFinalize(te_id || displayExercise?.te_id);
      if (response?.code === "action_in_flight") return;
      if (response?.status === "success") {
        setSnack({
          open: true,
          message:
            response.message || t("exerciseStopped") || "Exercise skipped",
          severity: "success",
        });
      } else {
        setSnack({
          open: true,
          message:
            response?.message ||
            t("errorStoppingExercise") ||
            "Error skipping exercise",
          severity: "error",
        });
      }
    },
    [
      coachLiveContext?.exercises,
      displayExercise?.te_id,
      emitFinalize,
      t,
    ]
  );

  const finalizeExercise = useCallback(async () => {
    if (!displayExercise?.te_id) return;
    const confirmed = confirmFinalizeExercise({
      sequences: displayExercise?.sequences,
      currentSequence: displayExercise?.currentSequence,
      awaitingManualSequenceStart: displayExercise?.awaitingManualSequenceStart,
      t,
    });
    if (!confirmed) return;
    const response = await emitFinalize(displayExercise.te_id);
    if (response?.code === "action_in_flight") return;
    if (response?.status === "success") {
      setSnack({
        open: true,
        message:
          response.message || t("exerciseStopped") || "Exercise stopped",
        severity: "success",
      });
    } else {
      setSnack({
        open: true,
        message:
          response?.message ||
          t("errorStoppingExercise") ||
          "Error stopping exercise",
        severity: "error",
      });
    }
  }, [
    displayExercise?.te_id,
    displayExercise?.sequences,
    displayExercise?.currentSequence,
    displayExercise?.awaitingManualSequenceStart,
    emitFinalize,
    t,
  ]);

  const isKotRunning =
    !!targetRaw &&
    isKotExercise(targetRaw) &&
    stateId === TE_STATE_STARTED;

  const kotCoachRound = useKotCoachRound({
    socket,
    teId: displayExercise?.te_id ?? null,
    tsId,
    exerciseStateId: stateId,
    enabled: isKotRunning && !!tsId && !!displayExercise?.te_id,
  });

  const kotMode = normalizeKotMode(
    kotCoachRound.snapshot?.config?.kot_mode ||
      targetRaw?.kot_config?.kot_mode
  );

  const handleCoachStopRound = useCallback(() => {
    kotCoachRound.requestStopRound();
  }, [kotCoachRound]);

  useEffect(() => {
    if (!kotCoachRound.stopRoundError) return;
    const key = kotCoachRound.stopRoundError;
    const msg =
      key.startsWith("kot") || key.length < 40 ? t(key) || key : key;
    setSnack({ open: true, message: msg, severity: "error" });
    kotCoachRound.clearStopRoundError();
  }, [kotCoachRound.stopRoundError, kotCoachRound, t]);

  const kotStopRoundSlot =
    isKotRunning &&
    kotMode === "STOP_SCORE_LEAD" &&
    kotCoachRound.canRequestStopRound ? (
      <KotCoachStopRoundButton
        visible
        stopping={kotCoachRound.stoppingRound}
        onStop={handleCoachStopRound}
        className="kot-coach-stop__button--actions-bar"
      />
    ) : null;

  const kotStartRoundSlot =
    isKotRunning && kotCoachRound.awaitingCoachStart ? (
      <KotCoachStartRoundButton
        canStartRound={kotCoachRound.canStartRound}
        awaitingCoachStart={kotCoachRound.awaitingCoachStart}
        preparingNextRound={kotCoachRound.preparingNextRound}
        roundNumber={kotCoachRound.roundNumber}
        starting={kotCoachRound.startingRound}
        onStart={kotCoachRound.startNextRound}
        className="kot-coach-start__button--actions-bar"
      />
    ) : null;

  const openFullControl = useCallback(() => {
    if (!selectedSession?.ts_id) return;
    navigate("/CoachExerciseControl", {
      state: { session: selectedSession, selectedgrp },
    });
  }, [navigate, selectedSession, selectedgrp]);

  if (!visible) return null;

  if (!tsId || !displayExercise || !targetRaw) {
    return null;
  }

  const terminal =
    stateId === TE_STATE_FINALIZED ||
    stateId === TE_STATE_CANCELLED ||
    stateId === "finalized" ||
    stateId === "cancelled";

  return (
    <>
      <Box
        sx={{
          position: "fixed",
          left: 0,
          right: 0,
          top: 0,
          zIndex: 1200,
          px: { xs: 1, sm: 2 },
          pt: "max(8px, env(safe-area-inset-top))",
          pb: 0.5,
          pointerEvents: "none",
          "& > *": { pointerEvents: "auto" },
        }}
      >
        <Box
          sx={{
            maxWidth: 560,
            mx: "auto",
            boxShadow: "0 4px 24px rgba(0,0,0,0.12)",
            borderRadius: "0 0 12px 12px",
            overflow: "hidden",
          }}
        >
          <CoachExerciseActionsBar
            exercise={displayExercise}
            stateId={stateId}
            brandPrimary={brandPrimary}
            brandPrimaryDark={brandPrimaryDark}
            compact
            onStart={startExercise}
            onPause={pauseExercise}
            onResume={resumeExercise}
            onFinalize={finalizeExercise}
            onStartNextSequence={startNextSequence}
            onSkipSequence={skipSequence}
            onSkipExercise={skipExercise}
            onOpenFullControl={openFullControl}
            kotStartRoundSlot={kotStartRoundSlot}
            kotStopRoundSlot={kotStopRoundSlot}
          />
        </Box>
      </Box>

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        sx={{
          top: terminal ? 72 : { xs: 88, sm: 92 },
        }}
      >
        <Alert
          severity={snack.severity}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          sx={{ width: "100%" }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </>
  );
}
